import { LogLevelDesc } from "loglevel";
import { Client as Conversations, ClientOptions, Conversation, User } from "@twilio/conversations";
import type { ConnectionState as ConversationsConnectionState } from "@twilio/conversations";
import packageInfo from "package.json";
import { ClientEventEmitter } from "~/modules/client/ClientEventEmitter/ClientEventEmitter";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { LogLevel } from "~/modules/cbm";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import type { Session } from "~/modules/session/Session/Session";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { ConversationsSdk } from "~/modules/conversations/ConversationSdk";
import { getConfigRegion, parseRegionForTwilsock } from "~/utils/regionUtil";
import { ClientEvent } from "~/modules/client";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";
import {
    INTERNAL_SDK_CONNECTION_STATE_CHANGED,
    InternalSdkConnectionState
} from "~/modules/client/ClientEventEmitter/InternalSdkEvents";

export class ConversationsSdkImpl implements ConversationsSdk {
    readonly #logger: Logger;

    readonly #session: Session;

    readonly #clientOptions: ClientOptionsStore;

    isInitialized: boolean = false;

    conversationsClient!: Conversations;

    #eventListenersRegistered: boolean = false;

    readonly #onConversationsConnectionStateChanged = (state: ConversationsConnectionState): void => {
        const clientEventEmitter = this.#ctx.retrieveInstanceOf(ContextInstance.ClientEventEmitter, ClientEventEmitter);
        let mapped: InternalSdkConnectionState;
        switch (state) {
            case "connected":
                mapped = "connected";
                break;
            case "denied":
            case "error":
            case "disconnected":
                mapped = "disconnected";
                break;
            default:
                
                mapped = "connecting";
        }
        clientEventEmitter.emit(INTERNAL_SDK_CONNECTION_STATE_CHANGED, "conversations", mapped);
    };

    readonly #ctx: ContextManager;

    constructor(ctx: ContextManager) {
        this.#ctx = ctx;
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#logger = getLogger(ctx)(LoggerName.Conversations);
        this.#logger.debug("ConversationsSdk constructed");
    }

    async init(): Promise<void> {
        if (this.isInitialized) {
            return Promise.resolve();
        }

        this.#registerEventListeners();
        const configRegion = getConfigRegion(this.#clientOptions);

        const parsedRegion = parseRegionForTwilsock(configRegion);

        const sdkVersion = packageInfo.version;
        const conversationsOptions: ClientOptions = {
            logLevel: this.mapToLogLevel(this.#clientOptions?.logger?.level),
            clientMetadata: { app: "flex-sdk", appv: sdkVersion }
        };

        if (parsedRegion !== undefined) {
            conversationsOptions.region = parsedRegion;
        }

        this.conversationsClient = new Conversations(this.#session.token, conversationsOptions);

        return new Promise<void>((resolve, reject) => {
            const onConnectionStateChanged = (state: ConversationsConnectionState) => {
                if (state === "connected") {
                    this.conversationsClient.removeListener("connectionStateChanged", onConnectionStateChanged);
                    this.isInitialized = true;
                    this.conversationsClient.on("connectionStateChanged", this.#onConversationsConnectionStateChanged);
                    this.#onConversationsConnectionStateChanged("connected");
                    resolve();
                } else if (state === "denied" || state === "error") {
                    this.conversationsClient.removeListener("connectionStateChanged", onConnectionStateChanged);
                    this.#onConversationsConnectionStateChanged(state);
                    reject(
                        createFlexSdkError(this.#ctx)(
                            ErrorCode.ConversationsSdkConnectionInvalidState,
                            { module: "conversations", severity: ErrorSeverity.Error, source: "ConversationsSdkImpl" },
                            `Conversations: Connection state changed to ${state}`
                        )
                    );
                }
            };

            this.conversationsClient.on("connectionStateChanged", onConnectionStateChanged);
        });
    }

    async updateToken(token: string): Promise<void> {
        await this.conversationsClient.updateToken(token);
    }

    



    async peekConversationBySid(conversationSid: string): Promise<Conversation> {
        
        
        return this.conversationsClient.peekConversationBySid(conversationSid);
    }

    async getUser(identity: string): Promise<User> {
        
        
        return this.conversationsClient.getUser(identity);
    }

    



    async getConversationBySid(conversationSid: string): Promise<Conversation> {
        

        
        return this.conversationsClient.getConversationBySid(conversationSid);
    }

    private mapToLogLevel(logLevel: LogLevelDesc): LogLevel {
        if (typeof logLevel === "number") {
            switch (logLevel) {
                case 0:
                    return LogLevel.Trace;
                case 1:
                    return LogLevel.Debug;
                case 2:
                    return LogLevel.Info;
                case 3:
                    return LogLevel.Warn;
                case 4:
                    return LogLevel.Error;
                case 5:
                    return LogLevel.Silent;
                default:
                    return LogLevel.Warn;
            }
        } else if (typeof logLevel === "string") {
            const lowercaseLogLevel = logLevel.toLowerCase();
            if (Object.values(LogLevel).includes(lowercaseLogLevel as LogLevel)) {
                return lowercaseLogLevel as LogLevel;
            }
        }
        return LogLevel.Warn;
    }

    destroy(): Promise<void> {
        if (this.isInitialized) {
            this.#onConversationsConnectionStateChanged("disconnected");
        }
        this.conversationsClient?.removeListener("connectionStateChanged", this.#onConversationsConnectionStateChanged);
        return this.conversationsClient.shutdown();
    }

    


    #registerEventListeners(): void {
        if (this.#eventListenersRegistered) return;
        this.#logger.debug("Conversations SDK: Registering client event listeners");

        const clientEventEmitter = this.#ctx.retrieveInstanceOf(ContextInstance.ClientEventEmitter, ClientEventEmitter);

        clientEventEmitter.addListener(ClientEvent.TokenUpdated, (token: string) => {
            void this.updateToken(token);
        });
        clientEventEmitter.addListener(ClientEvent.ClientDestroyed, () => {
            void this.destroy();
        });
        this.#eventListenersRegistered = true;
    }
}
